import React, {useState} from 'react'
import {BlockProps} from '../atoms/blockList'
import {CodeSnippetValue} from './types'
import {I18nWrapper} from '../atoms/i18nWrapper'
import {Button, Drawer, Form, Input, InputNumber, Panel, SelectPicker} from 'rsuite'
import sanitizeHtml from 'sanitize-html'
import {useTranslation} from 'react-i18next'
import './codeSnippetBlock.less'

export type CodeSnippetBlockProps = BlockProps<CodeSnippetValue>

const ratioPresets = ['16:9', '16:7', '5:8', '4:3', '1:1', '3:4', '8:5', '9:16']

const CUSTOM_RATIO = 'custom'

const ratioOptions = [
  ...ratioPresets.map((ratio) => ({label: ratio, value: ratio})),
  {label: 'Custom…', value: CUSTOM_RATIO},
]

const layouts = [
  {
    label: 'Aspect Ratio',
    value: 'aspectRatio',
  },
  {
    label: 'Fixed Height',
    value: 'fixedHeight',
  },
]

// The height of the Spotify episode player, the most common fixed-height embed.
const DEFAULT_FIXED_HEIGHT = 232

// Keep in sync with RATIO_PATTERN in the website's ModBlockSnippet.tsx. The `aspectratio_` form
// is the enum value that records saved before RESNF-1089 still hold.
const RATIO_PATTERN = /^(?:aspectratio_)?(\d+(?:\.\d+)?)\s*[:/_]\s*(\d+(?:\.\d+)?)$/

/** Returns the ratio as `width:height`, or `undefined` when the value is not a valid ratio. */
function normalizeRatio(value?: string): string | undefined {
  const match = value ? RATIO_PATTERN.exec(value.trim()) : null
  if (!match) return undefined
  const width = Number(match[1])
  const height = Number(match[2])
  return width > 0 && height > 0 ? `${width}:${height}` : undefined
}

function previewBoxStyle(size: CodeSnippetValue['size']): React.CSSProperties {
  if (size?.fixedHeight?.desktop) {
    return {height: size.fixedHeight.desktop}
  }
  const ratio = normalizeRatio(size?.aspectRatio?.desktop)
  if (ratio) {
    const [width, height] = ratio.split(':').map(Number)
    return {paddingTop: `${(height / width) * 100}%`}
  }
  return {paddingTop: '56.25%'}
}

/**
 * Picks a preset ratio or, through "Custom…", any ratio typed as `width:height`. Reports only
 * valid ratios, so an unfinished entry never reaches the stored value.
 */
function RatioPicker({value, onChange}: {value?: string; onChange: (ratio: string) => void}) {
  const normalized = normalizeRatio(value) ?? ratioPresets[0]
  const [isCustom, setCustom] = useState(() => !ratioPresets.includes(normalized))
  const [draft, setDraft] = useState(normalized)

  return (
    <>
      <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
        <SelectPicker
          placement="autoVerticalStart"
          preventOverflow
          data={ratioOptions}
          value={isCustom ? CUSTOM_RATIO : normalized}
          cleanable={false}
          searchable={false}
          onChange={(val) => {
            if (!val) return
            if (val === CUSTOM_RATIO) {
              setCustom(true)
              setDraft(normalized)
              return
            }
            setCustom(false)
            setDraft(val)
            onChange(val)
          }}
        />
        {isCustom && (
          <Input
            style={{width: 200}}
            placeholder="e.g. 9:17"
            value={draft}
            onChange={(val) => {
              setDraft(val)
              const ratio = normalizeRatio(val)
              if (ratio) onChange(ratio)
            }}
          />
        )}
      </div>
      {isCustom && (
        <Form.HelpText>
          {normalizeRatio(draft)
            ? 'Width and height, for example 9:17.'
            : 'Enter width and height separated by a colon, for example 9:17.'}
        </Form.HelpText>
      )}
    </>
  )
}

function sanitize(dirty: string) {
  return sanitizeHtml(dirty, {
    allowedTags: [
      'address',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'dd',
      'div',
      'dl',
      'dt',
      'hr',
      'li',
      'main',
      'ol',
      'p',
      'pre',
      'ul',
      'a',
      'abbr',
      'b',
      'br',
      'dfn',
      'em',
      'i',
      'kbd',
      'mark',
      'q',
      'rb',
      'rp',
      'rt',
      's',
      'samp',
      'small',
      'span',
      'strong',
      'sub',
      'sup',
      'time',
      'u',
      'var',
      'wbr',
      'caption',
      'col',
      'colgroup',
      'table',
      'tbody',
      'td',
      'tfoot',
      'th',
      'thead',
      'tr',
      'iframe',
    ],
    allowedAttributes: {
      td: ['bgcolor'],
      a: ['href', 'target'],
      iframe: ['src', 'width', 'height', 'title'],
    },
  })
}

function addSnippetStyles(html: string) {
  if (!html) return html
  const clean = sanitize(html)
  return clean.replace(/<iframe(\s|>)/gi, (match, p1) => {
    if (/style\s*=/.test(match)) {
      return match.replace(/style\s*=\s*"/i, 'style="border-radius:6px; overflow:hidden; ')
    }
    return `<iframe style="border-radius:6px; overflow:hidden; width:100%; height:100%; border:none;"${p1}`
  })
}

export function CodeSnippetBlock({
  value,
  onChange,
  languageLaneL,
  languageLaneR,
}: CodeSnippetBlockProps) {
  const {snippet, size} = value
  const {t} = useTranslation()
  const [isChooseModalOpen, setChooseModalOpen] = useState(false)

  return (
    <>
      <Button size={'md'} appearance={'ghost'} onClick={() => setChooseModalOpen(true)}>
        {'Edit iFrame'}
      </Button>
      <br />
      <br />
      <I18nWrapper
        lane1={
          <div
            style={{
              position: 'relative',
              width: '100%',
            }}>
            <div
              style={{
                position: 'absolute',
                zIndex: 1,
                height: '100%',
                left: 0,
              }}></div>
            <div
              style={{
                position: 'relative',
                ...previewBoxStyle(size),
                width: '100%',
              }}>
              {
                <div
                  className="snippet-wrapper"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    cursor: 'not-allowed',
                    borderRadius: 6,
                    overflow: 'hidden',
                  }}
                  dangerouslySetInnerHTML={{__html: addSnippetStyles(snippet[languageLaneL] || '')}}
                />
              }
            </div>
          </div>
        }
        lane2={
          languageLaneR ? (
            <div
              style={{
                position: 'relative',
                width: '100%',
                borderRadius: 6,
                opacity: 0.7,
                overflow: 'hidden',
                cursor: 'not-allowed',
              }}>
              <div
                style={{
                  position: 'relative',
                  ...previewBoxStyle(size),
                  width: '100%',
                }}>
                {
                  <div
                    className="snippet-wrapper"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: 6,
                      overflow: 'hidden',
                      pointerEvents: 'none',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: addSnippetStyles(snippet[languageLaneR] || ''),
                    }}
                  />
                }
              </div>
            </div>
          ) : null
        }
      />

      <Drawer
        open={isChooseModalOpen}
        style={{overflow: 'hidden'}}
        placement={'bottom'}
        size="full"
        onClose={() => setChooseModalOpen(false)}>
        <Drawer.Header>
          <Drawer.Title>{t('blockList.codeSnippet')}</Drawer.Title>
          <Drawer.Actions>
            <Button
              appearance={'primary'}
              color={'green'}
              onClick={() => {
                setChooseModalOpen(false)
                // persist the snippet with injected iframe styles
                onChange(addSnippetStyles(snippet[languageLaneL] || ''), ['snippet', languageLaneL])
              }}>
              {t('blockList.apply')}
            </Button>
          </Drawer.Actions>
        </Drawer.Header>
        <Drawer.Body style={{overflow: 'auto'}}>
          <I18nWrapper
            lane1={
              <Form>
                <div style={{marginBottom: 20}}>
                  <p>
                    <strong>{t('blocks.snippet.panels.noteTitle')}</strong>
                    {t('blocks.snippet.panels.note')}
                  </p>
                  <p>
                    <strong>{t('blocks.snippet.panels.instructions')}</strong>
                  </p>
                  <ul>
                    <li>{t('blocks.snippet.panels.supported_platforms')}</li>
                    <li>{t('blocks.snippet.panels.iframe_usage')}</li>
                    <li>{t('blocks.snippet.panels.interactive_content')}</li>
                  </ul>
                  <p>
                    <strong>{t('blocks.snippet.panels.sanitizeTitle')}</strong>
                    {t('blocks.snippet.panels.sanitize')}
                  </p>
                </div>

                <Form.Group controlId="snippet">
                  <Input
                    as="textarea"
                    rows={10}
                    value={snippet[languageLaneL] || ''}
                    onChange={(val) => onChange(val, ['snippet', languageLaneL])}
                    onBlur={() => {
                      onChange(addSnippetStyles(snippet[languageLaneL] || ''), [
                        'snippet',
                        languageLaneL,
                      ])
                    }}
                  />
                  <br />
                  <br />
                  <Button
                    onClick={() => {
                      onChange(addSnippetStyles(snippet[languageLaneL] || ''), [
                        'snippet',
                        languageLaneL,
                      ])
                    }}>
                    Sanitize iFrame
                  </Button>
                </Form.Group>
                <Form.Group controlId="layout">
                  <Form.ControlLabel>Layout</Form.ControlLabel>
                  <SelectPicker
                    placement="autoVerticalStart"
                    preventOverflow
                    data={layouts}
                    value={
                      size?.fixedHeight
                        ? 'fixedHeight'
                        : size?.aspectRatio
                          ? 'aspectRatio'
                          : undefined
                    }
                    searchable={false}
                    onChange={(val) => {
                      if (val === 'aspectRatio') {
                        onChange(
                          {
                            aspectRatio: {
                              desktop: ratioPresets[0],
                              mobile: ratioPresets[0],
                            },
                          },
                          ['size']
                        )
                      } else if (val === 'fixedHeight') {
                        onChange({fixedHeight: {desktop: DEFAULT_FIXED_HEIGHT}}, ['size'])
                      } else {
                        onChange(undefined, ['size'])
                      }
                    }}
                  />
                </Form.Group>
                {size?.aspectRatio && (
                  <>
                    <Form.Group controlId="desktop">
                      <Form.ControlLabel>Aspect Ratio Desktop/Tablet</Form.ControlLabel>
                      <RatioPicker
                        value={size.aspectRatio.desktop}
                        onChange={(ratio) => {
                          onChange({aspectRatio: {...size.aspectRatio, desktop: ratio}}, ['size'])
                        }}
                      />
                    </Form.Group>
                    <Form.Group controlId="mobile">
                      <Form.ControlLabel>Aspect Ratio Mobile</Form.ControlLabel>
                      <RatioPicker
                        value={size.aspectRatio.mobile}
                        onChange={(ratio) => {
                          onChange({aspectRatio: {...size.aspectRatio, mobile: ratio}}, ['size'])
                        }}
                      />
                    </Form.Group>
                  </>
                )}
                {size?.fixedHeight && (
                  <>
                    <Form.Group controlId="heightDesktop">
                      <Form.ControlLabel>Height Desktop/Tablet</Form.ControlLabel>
                      <InputNumber
                        style={{width: 200}}
                        postfix="px"
                        min={1}
                        step={1}
                        value={size.fixedHeight.desktop}
                        onChange={(val) => {
                          const height = Math.round(Number(val))
                          // The desktop height is required, so a cleared field keeps the last
                          // valid height instead of storing an empty value.
                          if (!(height > 0)) return
                          onChange({fixedHeight: {...size.fixedHeight, desktop: height}}, ['size'])
                        }}
                      />
                    </Form.Group>
                    <Form.Group controlId="heightMobile">
                      <Form.ControlLabel>Height Mobile</Form.ControlLabel>
                      <InputNumber
                        style={{width: 200}}
                        postfix="px"
                        min={1}
                        step={1}
                        placeholder={String(size.fixedHeight.desktop)}
                        value={size.fixedHeight.mobile ?? null}
                        onChange={(val) => {
                          const height = Math.round(Number(val))
                          onChange(
                            {
                              fixedHeight: {
                                ...size.fixedHeight,
                                mobile: val !== '' && val !== null && height > 0 ? height : null,
                              },
                            },
                            ['size']
                          )
                        }}
                      />
                      <Form.HelpText>Leave empty to use the desktop height.</Form.HelpText>
                    </Form.Group>
                  </>
                )}
              </Form>
            }
            lane2={
              languageLaneR ? (
                <Input as="textarea" rows={10} value={snippet[languageLaneR]} disabled={true} />
              ) : null
            }></I18nWrapper>
        </Drawer.Body>
      </Drawer>
    </>
  )
}
